import React, { useState } from "react";
import MicButton from "./MicButton";
import { Aurora, Card, PageHeader, Logo, Stepper, StopButton } from "./Shell";
import Instructions from "./Instructions";
import { useDemo, useDemoStep } from "./DemoContext";

const PHQ4 = [
  "Feeling nervous, anxious or on edge",
  "Not being able to stop or control worrying",
  "Feeling down, depressed or hopeless",
  "Little interest or pleasure in doing things",
];
const GAD7 = [
  "Feeling nervous, anxious, or on edge",
  "Not being able to stop or control worrying",
  "Worrying too much about different things",
  "Trouble relaxing",
  "Being so restless that it's hard to sit still",
  "Becoming easily annoyed or irritable",
  "Feeling afraid as if something awful might happen",
];
const FREQ_OPTS = [
  { label: "Not at all", value: 0 },
  { label: "Several days", value: 1 },
  { label: "More than half", value: 2 },
  { label: "Nearly every day", value: 3 },
];

export default function SymptomCheckIn({
  phase, // "pre" | "post"
  onComplete,
  onStop,
  stepIndex,
  steps,
  initialSuds = null, // L2 Adaptive Routing — prefill from Welcome's activation reading
}) {
  const [phq4, setPhq4] = useState(Array(4).fill(null));
  const [gad7, setGad7] = useState(Array(7).fill(null));
  const [suds, setSuds] = useState(
    phase === "pre" && initialSuds !== null && initialSuds !== undefined
      ? initialSuds
      : null
  );
  // Post-session only — free-text capture of what came up in the chat with
  // the Practice Coach so it lands in the session record's notes field
  // (and from there, the Notion "Notes" column for the wellness team).
  const [reflection, setReflection] = useState("");

  const phq4Total = phq4.reduce((a, b) => (b == null ? a : a + b), 0);
  const gad7Total = gad7.reduce((a, b) => (b == null ? a : a + b), 0);
  const allAnswered =
    phq4.every((v) => v !== null) && gad7.every((v) => v !== null) && suds !== null;

  const submit = () => {
    onComplete({
      phq4: phq4Total,
      gad7: gad7Total,
      suds,
      reflection: phase === "post" ? (reflection.trim() || null) : null,
    });
  };

  // Demo auto-fill: realistic pre vs. post values; submit with explicit
  // values (avoiding the stale-closure trap on phq4/gad7/suds state).
  const demo = useDemo();
  useDemoStep(() => {
    let payload;
    if (phase === "pre") {
      setPhq4([2, 2, 1, 1]);
      setGad7([2, 2, 2, 1, 1, 1, 1]);
      setSuds(7);
      payload = { phq4: 6, gad7: 10, suds: 7 };
    } else {
      setPhq4([1, 1, 0, 0]);
      setGad7([1, 1, 1, 0, 0, 0, 0]);
      setSuds(3);
      payload = { phq4: 2, gad7: 3, suds: 3 };
    }
    setTimeout(() => {
      if (demo.active) onComplete(payload);
    }, 1800);
  }, 1500);

  const eyebrow =
    phase === "pre" ? "Step 02 · Pre-Session Check-In" : "Step 06 · Post-Session Check-In";
  const title =
    phase === "pre"
      ? "How are you arriving today?"
      : "How are you leaving the session?";
  const subtitle =
    phase === "pre"
      ? "A brief check-in (PHQ-4, GAD-7, SUDS) — we'll compare with your post-session ratings."
      : "Same instruments. We'll compute the delta and a session summary on the next screen.";

  return (
    <Aurora>
      {phase === "post" && <StopButton onClick={onStop} />}
      <div className="relative max-w-4xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex items-center justify-between mb-12">
          <Logo size="md" />
          <Stepper steps={steps} current={stepIndex} />
        </div>

        <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle} />

        <Instructions
          title={
            phase === "pre"
              ? "Why we ask before the session"
              : "Why we ask again after the session"
          }
          items={
            phase === "pre"
              ? [
                  { strong: "PHQ-4", text: "a brief 4-question check-in on how you've been feeling over the last 2 weeks. Pick the box that best matches how often each statement applied to you." },
                  { strong: "GAD-7", text: "a more detailed check-in on worry and tension over the last 2 weeks. Same 0–3 frequency scale." },
                  { strong: "SUDS (0–10)", text: "where you are right now. 0 = completely calm, 5 = uncomfortable, 10 = overwhelming. Trust the first number that comes to mind." },
                  { strong: "These numbers personalize your session", text: "your SUDS choice will auto-tune the breathing pacer on the next screens — so be honest, not aspirational." },
                ]
              : [
                  { strong: "Same instruments, same scales", text: "share where you are right now, after the session." },
                  { strong: "Even a small drop counts", text: "a 1–2 point SUDS decrease is a meaningful nervous-system shift — you don't need a perfect 0." },
                  { strong: "If you're more activated", text: "that's useful information too. Your wellness team will see it and tune the next session." },
                  { strong: "Take 10 seconds before answering", text: "notice your body, your breath, your shoulders. Then rate." },
                ]
          }
        />

        <div className="space-y-6 fade-up">
          {/* PHQ-4 */}
          <Card>
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--accent-cyan)]">
                  PHQ-4 · Anxiety & Depression
                </div>
                <div className="text-ink text-xl font-light mt-1">
                  Over the last 2 weeks, how often…
                </div>
              </div>
              <div className="font-mono text-cyan-glow text-2xl">
                {phq4.every((v) => v !== null) ? phq4Total : "—"}
              </div>
            </div>
            <Likert items={PHQ4} values={phq4} setValues={setPhq4} testKey="phq4" />
          </Card>

          {/* GAD-7 */}
          <Card>
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--accent-cyan)]">
                  GAD-7 · Generalized Anxiety
                </div>
                <div className="text-ink text-xl font-light mt-1">
                  Over the last 2 weeks, how often…
                </div>
              </div>
              <div className="font-mono text-cyan-glow text-2xl">
                {gad7.every((v) => v !== null) ? gad7Total : "—"}
              </div>
            </div>
            <Likert items={GAD7} values={gad7} setValues={setGad7} testKey="gad7" />
          </Card>

          {/* SUDS */}
          <Card>
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--accent-cyan)]">
                  SUDS · Subjective Units of Distress
                </div>
                <div className="text-ink text-xl font-light mt-1">
                  Right now, how distressed do you feel? (0 = none · 10 = unbearable)
                </div>
              </div>
              <div className="font-mono text-cyan-glow text-4xl">
                {suds === null ? "—" : suds}
              </div>
            </div>
            <div className="grid grid-cols-11 gap-2" data-testid="suds-grid">
              {Array.from({ length: 11 }).map((_, i) => (
                <button
                  type="button"
                  key={`suds-${i}`}
                  className={`py-3 rounded-xl font-mono text-base transition-all border ${
                    suds === i
                      ? "bg-[color:var(--accent-cyan-muted)] border-[color:var(--accent-cyan)] text-[color:var(--accent-cyan)]"
                      : "border-[color:var(--hairline)] text-[color:var(--text-secondary)] hover:border-[color:rgba(15,26,46,0.20)] hover:text-white"
                  }`}
                  onClick={() => setSuds(i)}
                  data-testid={`suds-${i}-btn`}
                >
                  {i}
                </button>
              ))}
            </div>
          </Card>

          {/* Reflection — post-session only. Captures what surfaced in the
              chat with the Practice Coach so the wellness team can see it
              in Notion. Optional; the post-session form is still
              submittable without it. */}
          {phase === "post" && (
            <Card data-testid="post-reflection-card">
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--accent-cyan)]">
                    Reflection · From your chat with the coach
                  </div>
                  <div className="text-ink text-xl font-light mt-1">
                    What came up? Anything worth remembering.
                  </div>
                  <div className="text-sm text-[color:var(--text-secondary)] mt-1.5 leading-relaxed">
                    Paste or paraphrase a line or two from your chat with the
                    Practice Coach — an insight, a phrase, an intention. It
                    travels with this session so your wellness team can see it.
                  </div>
                </div>
                <div className="font-mono text-[10px] tracking-[0.20em] uppercase text-[color:var(--text-secondary)] whitespace-nowrap ml-4">
                  Optional
                </div>
              </div>
              <textarea
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                placeholder="e.g. 'Coach pointed out my shoulders dropped at minute 2.' · 'I want to try the slow exhale before my next meeting.'"
                rows={4}
                maxLength={800}
                className="w-full bg-[color:var(--bg-tint)] border border-[color:var(--hairline)] focus:border-[color:var(--accent-cyan)] outline-none rounded-xl px-4 py-3 text-base text-ink placeholder:text-soft transition-colors resize-y leading-relaxed"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                data-testid="post-reflection-textarea"
              />
              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <MicButton
                    size={36}
                    onTranscript={(text) =>
                      setReflection((prev) => (prev ? `${prev.trim()} ${text}` : text).slice(0, 800))
                    }
                    testId="reflection-mic-btn"
                  />
                  <span className="font-mono text-[10px] tracking-[0.20em] uppercase text-[color:var(--text-secondary)]">
                    Or speak it
                  </span>
                </div>
                <div
                  className="font-mono text-[10px] tracking-[0.20em] uppercase text-right text-[color:var(--text-secondary)]"
                  data-testid="post-reflection-counter"
                >
                  {reflection.length} / 800
                </div>
              </div>
            </Card>
          )}
        </div>

        <div className="mt-10">
          <button
            type="button"
            className="btn-primary"
            disabled={!allAnswered}
            onClick={submit}
            data-testid={`symptoms-${phase}-submit-btn`}
          >
            {phase === "pre" ? "Continue to Mode Selection →" : "View Session Summary →"}
          </button>
        </div>
      </div>
    </Aurora>
  );
}

function Likert({ items, values, setValues, testKey }) {
  return (
    <div className="space-y-3">
      {items.map((label, idx) => (
        <div
          key={`${testKey}-q${idx}-${label.slice(0, 32)}`}
          className="flex items-center justify-between gap-4 py-2 border-b border-[color:var(--hairline)] last:border-0"
        >
          <div className="text-[color:var(--text-primary)] text-sm sm:text-base flex-1">
            <span className="font-mono text-[color:var(--text-secondary)] mr-2">
              {String(idx + 1).padStart(2, "0")}.
            </span>
            {label}
          </div>
          <div className="flex gap-1">
            {FREQ_OPTS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                title={opt.label}
                onClick={() => {
                  const next = [...values];
                  next[idx] = opt.value;
                  setValues(next);
                }}
                className={`w-9 h-9 rounded-lg font-mono text-sm transition-all border ${
                  values[idx] === opt.value
                    ? "bg-[color:var(--accent-cyan-muted)] border-[color:var(--accent-cyan)] text-[color:var(--accent-cyan)]"
                    : "border-[color:var(--hairline)] text-[color:var(--text-secondary)] hover:border-[color:rgba(15,26,46,0.20)]"
                }`}
                data-testid={`${testKey}-q${idx}-${opt.value}`}
              >
                {opt.value}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
